import { Autocomplete, TextField } from '@mui/material';
import type { SyntheticEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { filterByNameOrGroup } from '../ColumnsList/filterByNameOrGroup';

export interface ColumnSelectOption {
  name: string;
  value: string;
  group: string;
  order: number;
}

interface Props {
  label: string;
  options: ColumnSelectOption[];
  disabledOptions?: string[];
  value: string;
  onChange: (v: string) => void;
}

export function ColumnSelect({
  label,
  options,
  disabledOptions,
  value,
  onChange,
}: Props) {
  const [optionValue, setOptionValue] = useState(() => {
    return getOptionValue(options, value);
  });
  const sortedOptions = useMemo(() => {
    const groupsMap = new Map<string, ColumnSelectOption[]>();

    options.forEach((option) => {
      const group = groupsMap.get(option.group) || [];

      group.push(option);
      groupsMap.set(option.group, group);
    });
    groupsMap.forEach((group) => {
      group.sort((colA, colB) => {
        return colA.order - colB.order;
      });
    });

    return [...groupsMap.entries()]
      .sort((groupA, groupB) => {
        return groupA[1][0].order - groupB[1][0].order;
      })
      .map(([_, value]) => {
        return value;
      })
      .flat();
  }, [options]);
  const disabledOptionsSet = useMemo(() => {
    return new Set(disabledOptions || []);
  }, [disabledOptions]);

  useEffect(() => {
    setOptionValue(getOptionValue(options, value));
  }, [value, options]);

  const handleChange = (_: SyntheticEvent, newOption: ColumnSelectOption) => {
    setOptionValue(newOption);
    onChange(newOption.value);
  };

  if (options.length === 0) {
    return null;
  }

  return (
    <Autocomplete
      sx={{
        width: 200,
      }}
      getOptionDisabled={(option) => {
        return disabledOptionsSet.has(option.value);
      }}
      disableClearable
      filterOptions={(options, { inputValue }) =>
        filterByNameOrGroup(options, inputValue)
      }
      options={sortedOptions}
      value={optionValue!} // "!"" for the case when optionValue not found
      groupBy={(option) => option.group}
      getOptionLabel={(option) => option.name}
      ListboxProps={{
        sx: {
          p: 0,
          fontSize: '0.875rem',
          lineHeight: '1.5rem',

          '& .MuiListSubheader-root': {
            px: 2,
            py: 1,
            fontSize: '0.875rem',
            lineHeight: '1rem',
            fontWeight: 500,
            color: '#343844',
            backgroundColor: '#F1F3F8',
            top: 0,

            '&:empty': {
              display: 'none',
            },
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          InputLabelProps={{
            sx: {
              fontSize: '0.875rem',
              transform: optionValue
                ? 'translate(16px, -7px) scale(0.75)'
                : 'translate(16px, 10px) scale(1)',

              '&.Mui-focused': {
                transform: 'translate(16px, -7px) scale(0.75)',
              },
            },
          }}
          InputProps={{
            ...params.InputProps,
            sx: {
              height: 40,
              borderRadius: 2,
              fontSize: '0.875rem',

              '& .MuiOutlinedInput-notchedOutline > legend': {
                ml: 0.5,
                mr: -0.5,
                fontSize: '0.625rem',
              },
            },
          }}
        />
      )}
      isOptionEqualToValue={(option, v) => {
        return option.value === v.value;
      }}
      onChange={handleChange}
    />
  );
}

const getOptionValue = (
  options: ColumnSelectOption[],
  value: string,
): ColumnSelectOption | null => {
  return (
    options.find((option) => {
      return option.value === value;
    }) || null
  );
};
